import { memo, useCallback } from 'react';

import { useTrackChangedVideoClosedCaptionTrack } from '@air/analytics';
import { CheckIcon, ClosedCaptionIcon } from '@air/design-icons';
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel } from '@air/primitive-dropdown-menu';
import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { usePlayerStore } from './PlayerContextProvider';

const PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL = 'No subtitles';

export type PlayerSubtitlesButtonProps = Omit<IconButtonProps, 'children' | 'label'>;

export const PlayerSubtitlesButton = memo((props: PlayerSubtitlesButtonProps) => {
  const { disabled, ...restProps } = props;
  const id = usePlayerStore((state) => state.id);
  const hlsInstance = usePlayerStore((state) => state.hlsInstance);
  const setCaptionsEnabled = usePlayerStore((state) => state.setCaptionsEnabled);
  const subtitleTrack = usePlayerStore((state) => state.subtitleTrack);
  const subtitleTracks = usePlayerStore((state) => state.subtitleTracks);

  const isDefaultSubtitleTrack = subtitleTrack === -1;
  const hasSubtitles = !!subtitleTracks.length;
  const isDisabled = disabled || !hasSubtitles;
  const { trackChangedVideoClosedCaptionTrack } = useTrackChangedVideoClosedCaptionTrack();

  const handleTrackChange = useCallback(
    (trackIndex: number) => {
      if (hlsInstance) {
        hlsInstance.subtitleTrack = trackIndex;
      }

      setCaptionsEnabled(trackIndex >= 0);

      if (trackIndex >= 0) {
        trackChangedVideoClosedCaptionTrack({
          id,
          track: subtitleTracks[trackIndex].name || subtitleTracks[trackIndex].lang || `Track ${trackIndex + 1}`,
        });
      }
    },
    [hlsInstance, id, setCaptionsEnabled, trackChangedVideoClosedCaptionTrack, subtitleTracks],
  );

  const handleTrackOff = useCallback(() => {
    handleTrackChange(-1);
  }, [handleTrackChange]);

  if (!hasSubtitles) {
    return (
      <Tooltip label={PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL}>
        <IconButton
          appearance="ghost"
          color="gray"
          data-testid="PLAYER_SUBTITLES_BUTTON"
          label={PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL}
          size="medium"
          {...restProps}
          disabled={isDisabled}
        >
          <ClosedCaptionIcon size="sm" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      disabled={isDisabled}
      side="top"
      trigger={
        <IconButton
          appearance="ghost"
          color={isDefaultSubtitleTrack ? 'grey' : 'blue'}
          data-testid="PLAYER_SUBTITLES_BUTTON"
          label="Subtitles"
          size="medium"
          {...restProps}
          disabled={isDisabled}
        >
          <ClosedCaptionIcon size="sm" />
        </IconButton>
      }
    >
      <DropdownMenuLabel>Subtitles</DropdownMenuLabel>
      <DropdownMenuItem
        label="Off"
        onSelect={handleTrackOff}
        suffix={subtitleTrack === -1 ? <CheckIcon size="sm" /> : null}
      />
      {subtitleTracks.map((track, index) => (
        <DropdownMenuItem
          key={track.id}
          label={track.name || track.lang || `Track ${index + 1}`}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onSelect={() => handleTrackChange(index)}
          suffix={subtitleTrack === index ? <CheckIcon size="sm" /> : null}
        />
      ))}
    </DropdownMenu>
  );
});

PlayerSubtitlesButton.displayName = 'PlayerSubtitlesButton';
